const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/AppointementList/AppointmentListRow.tsx';
import moment, { now } from 'moment';
import { Icon, IconButton } from '@mui/material';
import React, { useState } from 'react';
import { StructuredAppointment } from '@cistec/common_web/defs/kisim';
import { AppointmentMenu } from './AppointmentMenu';
import { Theme, useTheme } from '@mui/material/styles';
import { css, cx } from '@emotion/css';
import { ExpandMore } from '@mui/icons-material';
import textMap from 'App/components/AppointementList/translations';
import { PhoneLinkComponent } from '../PhoneLink';
import { AttachmentList } from '../AttachmentList/AttachmentList';
import useBreakpoint from '@cistec/common_web/hooks/useBreakpoint';
import { useSelector } from 'react-redux';
import { getDisplayTime } from './utils';
import { getAppointmentObjectTitle } from 'App/utils/appointment';
import { useIntl } from 'react-intl';
import { convertStructuredAppointmentToAppointmentObject } from '@cistec/common_web/utils/kisim';
import { useHistory } from 'react-router';
import { goToAppointment } from 'App/utils/history';
import { useFeatureFlags } from 'App/store/featureFlags';
import { selectEmailFromRegisterWorkItem } from 'App/store/login';
import { useAppointmentAttachments } from 'App/store/appointmentAttachment';
import { LinkHrefTagFrame } from 'App/components/old_components/components/LinkFrame';
import { styleIcon } from 'App/pages/DashboardPage/Components/appointmentCards/styles';
import { useDurationText } from '@cistec/common_web/utils/dateTime/dateTime';
import { TelemedicineBtn } from '../Buttons/TelemedicineBtn';
import { SublimdBtn } from '../Buttons/SublimdButton';
import { getAppointmentStatusChip } from 'App/components/utils';

const appointmentCSS = (theme: Theme) => css`
    padding: ${theme.spacing(1)} ${theme.spacing(2)};
    line-height: 1.5em;

    &.expanded {
        position: relative;

        & .details {
            display: flex;
        }

        & .preview-icons {
            display: none;
        }

        & .expand-button {
            position: absolute;
            bottom: ${theme.spacing(1)};
            right: ${theme.spacing(2)};
            transform: rotate(180deg);
            padding: 0 !important;
        }
    }

    & .invisible {
        visibility: hidden;
    }

    &.not-expanded {
        & .expand-button {
            padding: 0;
        }
    }

    & .summary,
    & .details {
        display: flex;
        justify-content: space-between;
        gap: ${theme.spacing(2)};

        & > div:last-child {
            flex: auto;
        }
    }

    & .content-menu {
        display: flex;
        align-items: self-start;

        *:not(:first-child) {
            margin-left: 12px;
        }
    }

    & .appointmentActionRow {
        margin-top: ${theme.spacing(1)};
        margin-bottom: ${theme.spacing(0.5)};
        display: flex;
        gap: ${theme.spacing(1)};
    }

    & .title {
        font-weight: 500;
    }

    & .summary {
        border-left: 6px solid ${theme.palette.primary.main};
        padding-left: 10px;

        & .preview-icons {
            color: ${theme.palette.grey.A400};
            margin-top: ${theme.spacing(1)};
        }

        & .row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: ${theme.spacing(2)};

            & *:first-child {
                flex: auto;
            }

            & .appointment-confirmation-button {
                flex: none;
            }
        }
    }

    & .details {
        display: none;
        border-left: 4px dotted ${theme.palette.primary.main};
        margin-left: 1px;
        padding-left: 11px;

        & .content-column {
            display: grid;
            grid-template-columns: auto 1fr;
            column-gap: 8px;

            .row {
                display: flex;
                flex-direction: row;

                .label {
                    flex: none;
                    min-width: 5px;
                }
            }
        }
    }

    &.isSmallDevice .time-column {
        display: none;
    }

    .time-label {
        display: none;
    }

    &.isSmallDevice .time-label {
        display: inline;
    }

    & .geoCellStyle {
        color: ${theme.palette.primary.main};
        margin-top: 0;
    }
`;

export interface IAppointmentProps {
    appointment: StructuredAppointment;
}

export const AppointmentListRow = (props: IAppointmentProps) => {
    const { appointment } = props;
    const { formatMessage } = useIntl();
    const appointmentObject = convertStructuredAppointmentToAppointmentObject(appointment, formatMessage);
    const { practitioners, externalLink } = appointmentObject;
    const history = useHistory();
    const theme = useTheme();
    const isSmallDevice = useBreakpoint(theme?.breakpoints?.values?.sm);
    const currentPatEmail = useSelector(selectEmailFromRegisterWorkItem);

    const { data: featureFlags } = useFeatureFlags();
    const [expanded, setExpanded] = useState(false);

    const time = getDisplayTime(appointmentObject, formatMessage);
    const title = getAppointmentObjectTitle(appointmentObject, formatMessage, currentPatEmail);

    const durationText = useDurationText(appointment.STARTDAT, appointment.STOPDAT);

    const standort = appointment.RESSOURCENSTANDORT?.Standort;
    const locationAddress = appointment.RESSOURCENSTANDORT?.Adresse;
    const locationPhoneNumber = appointment.RESSOURCENSTANDORT?.TelefonStandort;

    const { data: attachments } = useAppointmentAttachments(appointment.TERMNR);
    const practitioner = practitioners?.[0]?.name;
    const isPastAppointment = moment(appointment.STARTDAT).isBefore(now());

    const handleDetailsToggleClick = () => {
        setExpanded(!expanded);
    };

    const showAttachments = featureFlags.appointment.appointmentDocs && !!attachments?.length;

    const showPractitioner = featureFlags.appointment.showResponsibilities && !!practitioner;
    const showDetails = Boolean(showPractitioner || locationPhoneNumber || showAttachments || durationText);

    const handleTitleClick = (event: any) => {
        if (process.env.NODE_ENV === 'development') {
            goToAppointment(history, appointment.TERMNR);
        }
    };
    const renderDuration = () => {
        if (appointment.TYP !== 'KONS') return null;

        return (
            <>
                <div className="label">{formatMessage(textMap.duration)}</div>
                <div>{durationText}</div>
            </>
        );
    };

    const renderFachperson = () => {
        if (!showPractitioner) return null;

        return (
            <>
                <div className="label">{formatMessage(textMap.fachperson)}</div>
                <div>{practitioner}</div>
            </>
        );
    };

    const renderLocation = () => {
        if (!standort && !locationAddress && !locationPhoneNumber) return null;

        return (
            <>
                <div className="label">{formatMessage(textMap.standort)}</div>
                <div>
                    {standort ? <div>{standort}</div> : null}
                    {locationAddress ? (
                        <LinkHrefTagFrame
                            className="geoCellStyle"
                            styleIcon={styleIcon}
                            text={locationAddress}
                            href={`https://maps.google.com/?q=${locationAddress}`}
                        />
                    ) : null}
                    {locationPhoneNumber ? (
                        <PhoneLinkComponent phone={locationPhoneNumber} className="geoCellStyle" />
                    ) : null}
                </div>
            </>
        );
    };

    const renderAttachmentList = () => {
        if (!showAttachments || !attachments) return null;

        return <AttachmentList attachments={attachments} />;
    };

    return (
        <div
            className={cx(`appointment_${appointment.TERMNR} ${appointment.TYP}`, appointmentCSS(theme), {
                expanded,
                isSmallDevice,
                'not-expanded': !expanded,
            })}
        >
            <div className={'summary'}>
                <div className={cx('time-column', { invisible: !time })}>{time || '00:00'}</div>
                <div className={'content-column'}>
                    <div className={'row'}>
                        <div className={'title'} onClick={handleTitleClick}>
                            {time && <span className={'time-label'}>{time} - </span>}
                            {title}
                        </div>
                        {!isPastAppointment && (
                            <>
                                <div>{getAppointmentStatusChip(appointment, formatMessage)}</div>
                                <div className={'content-menu'}>
                                    <AppointmentMenu appointment={appointment} />
                                </div>
                            </>
                        )}
                    </div>
                    <div className={'appointmentActionRow'}>
                        {!isPastAppointment && <TelemedicineBtn externalLink={externalLink} />}
                        {appointment.SUBLIMDSTATE ? <SublimdBtn termNr={appointment.TERMNR} /> : null}
                    </div>
                    {showDetails && (
                        <div className={'row'} onClick={handleDetailsToggleClick}>
                            <div className={'preview-icons'}>
                                {showPractitioner ? <Icon className={'icons8-user'} /> : null}
                                {locationPhoneNumber ? <Icon className={'icons8-phone'} /> : null}
                                {showAttachments ? <Icon className={'icons8-document'} /> : null}
                            </div>
                            <IconButton aria-expanded={expanded} aria-label="show more" className={'expand-button'}>
                                <ExpandMore />
                            </IconButton>
                        </div>
                    )}
                </div>
            </div>
            <div className={'details'}>
                <div className={cx('time-column', { invisible: true })}>{'00:00'}</div>
                <div className="content-column">
                    {renderDuration()}
                    {renderFachperson()}
                    {renderLocation()}
                    {renderAttachmentList()}
                </div>
            </div>
        </div>
    );
};
